import type {
  CuratedCollectionBase,
  CuratedCollectionLocaleMap,
  CuratedCollectionStrings,
  CuratedCollectionsFile
} from '@/types/collections'

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value)

const isNonEmptyString = (value: unknown): value is string =>
  typeof value === 'string' && value.trim().length > 0

const isFiniteInteger = (value: unknown): value is number =>
  typeof value === 'number' && Number.isInteger(value) && Number.isFinite(value)

function validateBaseCollection(
  collection: CuratedCollectionBase,
  slugKey: string
) {
  if (collection.slug !== slugKey) {
    throw new Error(`Slug mismatch for ${slugKey} (found ${collection.slug})`)
  }
  if (!isFiniteInteger(collection.level) || collection.level <= 0) {
    throw new Error(`Invalid level for ${slugKey}: ${collection.level}`)
  }
  if (
    collection.coverIcon !== undefined &&
    collection.coverIcon !== null &&
    !isNonEmptyString(collection.coverIcon)
  ) {
    throw new Error(`Invalid coverIcon for ${slugKey}`)
  }
  if (collection.entryIds !== undefined) {
    if (!Array.isArray(collection.entryIds)) {
      throw new Error(`entryIds must be an array for ${slugKey}`)
    }
    collection.entryIds.forEach((id, idx) => {
      if (!isFiniteInteger(id) || id <= 0) {
        throw new Error(`Invalid entryId at index ${idx} for ${slugKey}: ${id}`)
      }
    })
  }
  if (!isFiniteInteger(collection.item_count) || collection.item_count < 0) {
    throw new Error(
      `Invalid item_count for ${slugKey}: ${collection.item_count}`
    )
  }
}

export function assertCuratedCollectionsFile(
  data: unknown
): asserts data is CuratedCollectionsFile {
  if (!isPlainObject(data)) {
    throw new Error('Curated collections data must be an object')
  }
  if (!('generatedAt' in data) || !isNonEmptyString(data.generatedAt)) {
    throw new Error('generatedAt is required on curated collections data')
  }
  if (!('collections' in data) || !isPlainObject(data.collections)) {
    throw new Error(
      'collections object is required on curated collections data'
    )
  }

  const collections = data.collections as Record<string, CuratedCollectionBase>
  Object.entries(collections).forEach(([slug, collection]) => {
    validateBaseCollection(collection, slug)
  })
}

export type CuratedManifest = {
  collections: Record<
    string,
    {
      slug: string
      level: number
      coverIcon?: string | null
      item_count: number
    }
  >
}

export function assertCuratedManifest(
  data: unknown
): asserts data is CuratedManifest {
  if (!isPlainObject(data)) {
    throw new Error('Curated manifest must be an object')
  }
  if (!('collections' in data) || !isPlainObject(data.collections)) {
    throw new Error('collections object is required on curated manifest')
  }
  Object.entries(data.collections as Record<string, unknown>).forEach(
    ([slug, entry]) => {
      if (!isPlainObject(entry)) {
        throw new Error(`Manifest entry for ${slug} must be an object`)
      }
      if (!isNonEmptyString(entry.slug) || entry.slug !== slug) {
        throw new Error(`Manifest slug mismatch for ${slug}`)
      }
      if (!isFiniteInteger(entry.level) || entry.level <= 0) {
        throw new Error(`Invalid level for ${slug} in manifest`)
      }
      if (
        entry.coverIcon !== undefined &&
        entry.coverIcon !== null &&
        !isNonEmptyString(entry.coverIcon)
      ) {
        throw new Error(`Invalid coverIcon for ${slug} in manifest`)
      }
      if (!isFiniteInteger(entry.item_count) || entry.item_count < 0) {
        throw new Error(`Invalid item_count for ${slug} in manifest`)
      }
    }
  )
}

export function assertCuratedStrings(
  data: unknown
): asserts data is CuratedCollectionLocaleMap {
  if (!isPlainObject(data)) {
    throw new Error('Curated collection strings must be an object')
  }

  Object.entries(data).forEach(([locale, slugMap]) => {
    if (!isPlainObject(slugMap)) {
      throw new Error(`Strings for locale ${locale} must be an object`)
    }
    Object.entries(slugMap).forEach(([slug, strings]) => {
      if (!isPlainObject(strings)) {
        throw new Error(`Strings for ${slug} (${locale}) must be an object`)
      }
      const payload = strings as CuratedCollectionStrings
      if (!isNonEmptyString(payload.title)) {
        throw new Error(`title is required for ${slug} (${locale})`)
      }
      if (
        payload.description !== undefined &&
        payload.description !== null &&
        typeof payload.description !== 'string'
      ) {
        throw new Error(`description must be a string for ${slug} (${locale})`)
      }
      if (
        payload.coverLabel !== undefined &&
        payload.coverLabel !== null &&
        typeof payload.coverLabel !== 'string'
      ) {
        throw new Error(`coverLabel must be a string for ${slug} (${locale})`)
      }
    })
  })
}
